var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import '../form/MessageInput.js';
import '../card/MessageCard.js';
let ConversationPage = class ConversationPage extends LitElement {
    constructor() {
        super(...arguments);
        this.messages = [];
        this.loading = false;
        this.ws = null;
    }
    updated(changedProperties) {
        if (changedProperties.has('chatSession') && this.chatSession && this.apiService) {
            this.loadMessages();
            this.connectWebSocket();
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.disconnectWebSocket();
    }
    connectWebSocket() {
        this.disconnectWebSocket();
        if (!this.chatSession || !this.apiService)
            return;
        this.ws = this.apiService.websocket_chat_session(this.chatSession.chat_session_id);
        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.message) {
                    const newMessage = data.message;
                    const exists = this.messages.some(m => m.chat_message_id === newMessage.chat_message_id);
                    if (!exists) {
                        this.messages = this.sortMessages([...this.messages, newMessage]);
                        this.scrollToBottom();
                    }
                }
            }
            catch (error) {
                console.error('Failed to parse WebSocket message:', error);
            }
        };
    }
    disconnectWebSocket() {
        if (this.ws) {
            this.ws.stop?.();
            this.ws = null;
        }
    }
    scrollToBottom() {
        requestAnimationFrame(() => {
            const content = this.shadowRoot?.querySelector('.conversation-page__content');
            if (content) {
                content.scrollTop = content.scrollHeight;
            }
        });
    }
    sortMessages(messages) {
        return [...messages].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
    async loadMessages() {
        this.loading = true;
        try {
            const messages = await this.apiService.get(`/chat_session/${this.chatSession.chat_session_id}/chat_messages`);
            this.messages = this.sortMessages(messages);
            this.scrollToBottom();
        }
        catch (error) {
            console.error('Failed to load messages:', error);
            this.messages = [];
        }
        finally {
            this.loading = false;
        }
    }
    isCurrentUserMessage(message) {
        return message.created_by === this.currentUser;
    }
    getFilteredMessages() {
        const primaryTypes = ['message', 'question'];
        const secondaryTypes = ['action', 'agent', 'information', 'error'];
        const primaryMessages = this.messages.filter((m) => primaryTypes.includes(m.type));
        const lastSecondaryMessage = [...this.messages]
            .reverse()
            .find((m) => secondaryTypes.includes(m.type));
        if (lastSecondaryMessage) {
            return this.sortMessages([...primaryMessages, lastSecondaryMessage]);
        }
        return primaryMessages;
    }
    async sendMessage(messageText) {
        try {
            const sentMessage = await this.apiService.post(`/chat_session/${this.chatSession.chat_session_id}/chat_message`, {
                type: 'message',
                content: { text: messageText },
            });
            // Add the sent message to the list
            this.messages = this.sortMessages([...this.messages, sentMessage]);
            this.scrollToBottom();
        }
        catch (error) {
            console.error('Failed to send message:', error);
        }
    }
    async handleNewMessage(e) {
        e.stopPropagation();
        await this.sendMessage(e.detail);
    }
    async handleQuestionAnswer(e) {
        e.stopPropagation();
        await this.sendMessage(e.detail.option);
    }
    render() {
        const displayedMessages = this.getFilteredMessages();
        return html `
      <div class="conversation-page__content">
        ${this.loading
            ? html `<div class="conversation-page__loading">${msg('Loading...', { id: 'conversationPage.loading', desc: 'Message shown while the conversation is loading' })}</div>`
            : displayedMessages.map((message) => html `
                <div
                  class="conversation-page__message ${this.isCurrentUserMessage(message)
                ? 'conversation-page__message--right'
                : 'conversation-page__message--left'}"
                >
                  <message-card
                    .message=${message}
                    @question-answer=${this.handleQuestionAnswer}
                  ></message-card>
                </div>
              `)}
      </div>
      <div class="conversation-page__input">
        <message-input @new-message=${this.handleNewMessage}></message-input>
      </div>
    `;
    }
};
ConversationPage.styles = css `
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      background: #fff;
      box-sizing: border-box;
    }

    .conversation-page__content {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .conversation-page__message {
      max-width: 70%;
    }

    .conversation-page__message--left {
      align-self: flex-start;
    }

    .conversation-page__message--right {
      align-self: flex-end;
    }

    .conversation-page__input {
      padding: 16px;
      display: flex;
      justify-content: center;
    }

    .conversation-page__input message-input {
      width: 100%;
      max-width: 800px;
    }

    .conversation-page__loading {
      text-align: center;
      color: #666;
      padding: 16px;
    }
  `;
__decorate([
    property({ type: Object })
], ConversationPage.prototype, "chatSession", void 0);
__decorate([
    property({ type: Object })
], ConversationPage.prototype, "apiService", void 0);
__decorate([
    property({ type: String })
], ConversationPage.prototype, "currentUser", void 0);
__decorate([
    state()
], ConversationPage.prototype, "messages", void 0);
__decorate([
    state()
], ConversationPage.prototype, "loading", void 0);
ConversationPage = __decorate([
    localized(),
    customElement('conversation-page')
], ConversationPage);
export { ConversationPage };
//# sourceMappingURL=ConversationPage.js.map